import { determineChannel } from '../rs-analytics';

// This plugin is responsible for adding global app state that isn't specific
// to any particular tracking call to the payload.properties so other plugins have
// access to it.

/**
 * Generator that creates an analytics plugin responsible for adding properties to
 * payload.properties from the Rocketship app's Vuex store.
 * @param {Object} options - Plugin options
 * @param {{
 *  deployPath: string,
 *  pageName: string,
 *  affiliateId: string,
 *  isLoggedIn: boolean,
 * }} options.reactiveData Vue Reactive object.
 * @param {*} options.store Deprecated. Vuex store from Rocketship application.
 * @returns {import('analytics').AnalyticsPlugin}
 */
export default function createRocketshipDataAnalyticsPlugin ({ reactiveData, store } = {}) {
    if (!reactiveData && !store) {
        throw new Error('createRocketshipDataAnalyticsPlugin requires reactiveData or vuex store');
    }

    return {
        name: 'rocketship-data',
        pageStart: setPayloadProperties,
        trackStart: setPayloadProperties,
    };

    // This function needs to return a payload object for other plugin middlewares to use
    function setPayloadProperties ({ payload }) {
        const
            // TODO: remove `store` support in a future breaking change.
            deployPath = reactiveData?.deployPath || store?.state.app.deployPath,
            pageName =  reactiveData?.pageName || store?.state.ui.pageName;

        const newPayload =  {
            ...payload,
            ...{
                properties: {
                    ...payload.properties,
                    channel: determineChannel(),
                    loggedIn: !!(reactiveData?.isLoggedIn || store?.getters['profile/loggedIn']),
                    affiliateId: reactiveData?.affiliateId || store?.state.app.affiliateId || '',
                },
            },
        };

        // Only overwrite page property if page is available through the store
        // which allows #track() to take page as an argument
        const urchinPageName = buildUrchinPageName(deployPath, pageName);
        if (urchinPageName) {
            newPayload.properties.page = urchinPageName;
        }

        return newPayload;
    }
}

function buildUrchinPageName (deployPath, pageName) {
    if (!deployPath || !pageName) {
        return '';
    }
    // ! Page MUST be defined as /deploy/path/page
    const urchinPageName = `/${deployPath}/${pageName}`;
    // ! Page names have a limit of 50 characters including the deploy path of the promotion.
    if (urchinPageName.length >= 50) {
        const documentationLink = process.env.NODE_ENV !== 'production' ?
            'Refer to: https://confluence.helloworld.com/display/IT/Urchin+Data+Analytics+Tracking#UrchinDataAnalyticsTracking-HowdoItrackmyevent?' :
            '';
        console.warn(
            `Urchin error: page name is greater than 50 characters. ${documentationLink}`,
        );
    }
    return urchinPageName;
}
